// src/oura/components/newscan/QuestionList.tsx
//
// The 50 on the Review step, one GroupFold per display group: one per outlet
// for the location questions ("Bangsar · flagship", "Subang Jaya"; Online
// "Market · Malaysia"), then Specialty and Intent.
//
// While the draft takes edits every group starts open, so a read-through
// misses none (staff can still fold one). Read only (approved or started): a
// group with an open decision starts open, otherwise the first group is open
// and the rest are folded. Folded, a group still says what it holds
// ("1 to decide · 2 yours"), so a fold never hides a decision. One editor is
// open at a time; the parent holds which, so a decision's "Edit 44 myself"
// can open it. Lock and unlock live in the editor.
//
// `readOnly` (an approved or started draft): the rows are read, no editor opens.
//
// `searchOnly` (the health line's "11 match real searches" chip is on): one
// flat list of the questions with a search, most searched first, and a text
// button to show all of them again.
//
// Brand mode (v4.5): the groups are "Recommend you · problems", "Recommend
// you · products", "Cite your site" and "Business buyers", each with one
// intro line, and every row carries its market chip and where it came from.

import type { ScanDraftQuestion, ScanDraftWord } from "@service/aeo/scanDraftTypes";
import { TooltipProvider } from "@component/tooltip";
import { cn } from "@oura/lib/cn";
import { GroupFold, PillButton, Surface, TYPE, type Tone } from "@oura/ui";
import { plural } from "@oura/newscan/copy";
import type { MarketName } from "./brandReview";
import { QuestionRow, type QuestionEdit } from "./QuestionRow";

/** A group of rows as the Review step shows them. */
export interface DisplayGroup {
    /** Stable: `loc:<place>`, `specialty`, `intent`; brand `pick_problem`, `pick_product`, `source`, `supplier`. */
    id: string;
    title: string;
    tone: Tone;
    rows: ScanDraftQuestion[];
    /** One line under the title, inside the group (brand groups). */
    intro?: string;
}

const COPY = {
    toFix: (n: number) => `${n} to fix`,
    toDecide: (n: number) => `${n} to decide`,
    yours: (n: number) => `${n} yours`,
    locked: (n: number) => `${n} locked`,
    label: (n: number) => `All ${plural(n, "question")}`,
    bySearch: (n: number) => `${plural(n, "question")} by searches`,
    showAll: (n: number) => `Show all ${n}`,
} as const;

export interface QuestionListProps {
    groups: DisplayGroup[];
    words: Record<string, ScanDraftWord>;
    /** Open decisions per question number. */
    severity: Map<number, "blocker" | "decide">;
    /** The question whose editor is open. */
    openN: number | null;
    onOpen(n: number | null): void;
    onOpenWord(variable: string): void;
    /** An approved or started draft: rows read only, no editor. */
    readOnly?: boolean;
    /** Only the questions with a search, most searched first, in one list. */
    searchOnly?: boolean;
    /** Turns `searchOnly` off ("Show all 50"). */
    onShowAll?: () => void;
    /** Brand mode: the market a row is asked for, as its chip shows it. Omit on a local draft. */
    marketOf?: (q: ScanDraftQuestion) => MarketName | null;
    /** An edit of a finished scan: how that scan's question n was corrected, if it was ("Edited · V2", "Re-asking…"). */
    editOf?: (n: number) => QuestionEdit | null;
}

export function QuestionList({
    groups,
    words,
    severity,
    openN,
    onOpen,
    onOpenWord,
    readOnly = false,
    searchOnly = false,
    onShowAll,
    marketOf,
    editOf,
}: QuestionListProps) {
    const total = groups.reduce((n, g) => n + g.rows.length, 0);

    const close = (n: number, saved: boolean) => {
        onOpen(null);
        // Back to the wording that opened it, without moving the page.
        requestAnimationFrame(() => {
            document.querySelector<HTMLElement>(`[data-q-title="${n}"]`)?.focus({ preventScroll: !saved });
        });
    };

    const row = (q: ScanDraftQuestion) => (
        <QuestionRow
            key={q.n}
            question={q}
            words={words}
            severity={severity.get(q.n) ?? null}
            open={!readOnly && openN === q.n}
            onToggle={() => onOpen(openN === q.n ? null : q.n)}
            onClose={(saved) => close(q.n, saved)}
            onOpenWord={onOpenWord}
            readOnly={readOnly}
            market={marketOf?.(q) ?? null}
            edit={editOf?.(q.n) ?? null}
        />
    );

    if (searchOnly) {
        const searched = groups
            .flatMap((g) => g.rows)
            .filter((q) => q.search)
            .sort((a, b) => (b.search?.searches ?? 0) - (a.search?.searches ?? 0) || a.n - b.n);
        return (
            <TooltipProvider delayDuration={200}>
                <Surface flush className="px-5 py-1">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-3">
                        <h2 className={cn(TYPE.lab, "m-0 tabular-nums")}>{COPY.bySearch(searched.length)}</h2>
                        {onShowAll && (
                            <PillButton variant="text" onClick={onShowAll}>
                                {COPY.showAll(total)}
                            </PillButton>
                        )}
                    </div>
                    <div>{searched.map(row)}</div>
                </Surface>
            </TooltipProvider>
        );
    }

    return (
        <TooltipProvider delayDuration={200}>
            <Surface flush className="px-5 py-1">
                <h2 className="sr-only">{COPY.label(total)}</h2>
                <div>
                    {groups.map((g, i) => {
                        const fix = g.rows.filter((r) => severity.get(r.n) === "blocker").length;
                        const decide = g.rows.filter((r) => severity.get(r.n) === "decide").length;
                        const yours = g.rows.filter((r) => r.reworded).length;
                        const locked = g.rows.filter((r) => r.locked && !r.reworded).length;
                        const note = [
                            fix && COPY.toFix(fix),
                            decide && COPY.toDecide(decide),
                            yours && COPY.yours(yours),
                            locked && COPY.locked(locked),
                        ]
                            .filter(Boolean)
                            .join(" · ");
                        const hasIssue = fix + decide > 0;
                        // Taking edits: every group open for the read-through. Read only: issues and the first group.
                        const startOpen = !readOnly || hasIssue || i === 0;
                        return (
                            <GroupFold
                                key={g.id}
                                id={g.id}
                                tone={g.tone}
                                title={g.title}
                                count={g.rows.length}
                                lead="dot"
                                defaultFolded={!startOpen}
                                remember={false}
                                wrap
                                note={
                                    note ? (
                                        <span className={cn(TYPE.tc, hasIssue ? "text-gray-700" : "text-gray-500")}>{note}</span>
                                    ) : undefined
                                }
                            >
                                {g.intro && <p className={cn(TYPE.tc, "m-0 pb-1 pl-5 pt-0.5 text-gray-500")}>{g.intro}</p>}
                                <div>{g.rows.map(row)}</div>
                            </GroupFold>
                        );
                    })}
                </div>
            </Surface>
        </TooltipProvider>
    );
}
